import type { components } from '@/api/schema'

export const PRODUCT_SORT_VALUES = [
  'featured',
  'price_desc',
  'price_asc',
  'relevance',
  'newest',
] as const

export type ProductSort = (typeof PRODUCT_SORT_VALUES)[number]

export function isProductSort(value: unknown): value is ProductSort {
  return PRODUCT_SORT_VALUES.includes(value as ProductSort)
}

export type ProductsSearch = {
  q?: string
  page?: number
  per_page?: number
  price_min?: number
  price_max?: number
  sort?: ProductSort
}

export type RangeSelection = { min?: string; max?: string }
export type MultiSelections = Record<string, string[]>
export type RangeSelections = Record<string, RangeSelection>

export type ProductSearchState = {
  query: string
  perPage: number
  sort: ProductSort
  rangeSelections: RangeSelections
  resetPage: boolean
}

export type ShopifyFilterOption = {
  value: string
  label: string
  count?: number | null
  available_count?: number | null
  selected?: boolean
}

export type ShopifyFilterGroup = {
  key: string
  label: string
  type: 'multi_select' | 'range'
  options?: ShopifyFilterOption[]
  unit?: string | null
  min?: string | number | null
  max?: string | number | null
  selected?: {
    min?: string | number | null
    max?: string | number | null
  } | null
}

export type ShopifyFilterOptionsResponse = {
  query: { q?: string | null; selected: Record<string, unknown> }
  total: number
  price?: ShopifyFilterGroup | null
  facets: ShopifyFilterGroup[]
}

export type ShopifySearchProduct = {
  uuid: string
  title: string
  model: string
  vendor: {
    title: string
    shopify_id: string
    url: string
    thumbnail?: string | null
  }
  thumbnail: string | null
  shopify_id: string
  shopify_variant_id: string
  url: string
  price: string
  free_shipping: boolean
  // A Quick Ship listing's card: our warehouse's stock of this catalog product in one condition (USA-1573).
  quick_ship_listing_uuid?: string | null
  condition?: 'new' | 'open_box' | 'scratch_dent' | 'used' | null
  used_grade?: 'like_new' | 'good' | 'fair' | null
  available?: number | null
}

export type ShopifySearchResponse = {
  products: ShopifySearchProduct[]
  vendors: Array<{
    title: string
    shopify_id: string
    url: string
    thumbnail?: string | null
  }>
  categories: Array<{ title: string; shopify_id?: string; url: string }>
  total?: number
  page?: number
  per_page?: number
  total_pages?: number
  facets?: ShopifyFilterGroup[]
}

// Tracked for quoting but not in the storefront search (USA-1109).
export type QuotableProduct = components['schemas']['QuotableProductRead']

// A product a staff picker chose, from the storefront search or the quote-only results.
export type PickedProduct = {
  uuid: string
  title: string
  model: string
  manufacturer: string
  thumbnail: string | null
  // Sell price; null when the manufacturer is priced on each quote.
  price: string | null
  pricedOnEachQuote: boolean
  boughtThrough: string | null
  // Picked from a Quick Ship card: the line comes from our warehouse in this condition (USA-1574).
  warehouseSource?: { condition: 'new' | 'open_box' | 'scratch_dent' | 'used'; used_grade: 'like_new' | 'good' | 'fair' | null } | null
}

export type PageItem = number | 'ellipsis-left' | 'ellipsis-right'

export function sanitizeRangeInput(value: string) {
  return value.replace(/[^0-9.,-]/g, '').replace(/,/g, '')
}

export function rangeSearchValue(value: string | undefined): number | undefined {
  const normalized = value == null ? undefined : sanitizeRangeInput(value)
  if (!normalized) return undefined
  const parsed = Number(normalized)
  return Number.isFinite(parsed) ? parsed : undefined
}

export function productsSearchFromState(
  page: number,
  { query, perPage, rangeSelections, resetPage, sort = 'featured' }: ProductSearchState,
): ProductsSearch {
  const price = rangeSelections.price ?? {}
  const priceMin = rangeSearchValue(price.min)
  const priceMax = rangeSearchValue(price.max)
  return {
    ...(query ? { q: query } : {}),
    ...(page > 1 && !resetPage ? { page } : {}),
    ...(perPage !== 50 ? { per_page: perPage } : {}),
    ...(priceMin != null ? { price_min: priceMin } : {}),
    ...(priceMax != null ? { price_max: priceMax } : {}),
    ...(sort !== 'featured' ? { sort } : {}),
  }
}

const publicPageSizes = new Set([24, 50, 100])

function positiveInteger(value: unknown): number | undefined {
  const parsed = Number(value)
  return Number.isInteger(parsed) && parsed > 0 ? parsed : undefined
}

function optionalString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined
}

// Route validation rejects negatives; URL serialization intentionally does not.
export function optionalPrice(value: unknown): number | undefined {
  const normalized = sanitizeRangeInput(String(value ?? ''))
  if (!normalized) return undefined
  const parsed = Number(normalized)
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : undefined
}

export function validateProductsSearch(search: Record<string, unknown>): ProductsSearch {
  const page = positiveInteger(search.page)
  const perPage = positiveInteger(search.per_page)
  const q = optionalString(search.q)
  const priceMin = optionalPrice(search.price_min)
  const priceMax = optionalPrice(search.price_max)
  const sort = isProductSort(search.sort) ? search.sort : undefined
  return {
    ...(q ? { q } : {}),
    ...(page ? { page } : {}),
    ...(perPage && publicPageSizes.has(perPage) ? { per_page: perPage } : {}),
    ...(priceMin != null ? { price_min: priceMin } : {}),
    ...(priceMax != null ? { price_max: priceMax } : {}),
    ...(sort && sort !== 'featured' ? { sort } : {}),
  }
}
